import React, { useState, useEffect } from "react";
import {
  Box,
  Flex,
  Heading,
  Button,
  Badge,
  Tag,
  Text,
  VStack,
  HStack,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Card,
  CardHeader,
  CardBody,
  useToast,
  useColorModeValue,
  Skeleton,
  Divider,
  Icon,
  Tooltip,
  Progress,
  Alert,
  AlertIcon,
} from "@chakra-ui/react";
import {
  BsArrowLeft,
  BsArrowRepeat,
  BsPencil,
  BsCircleFill,
  BsClockHistory,
  BsShieldCheck,
  BsGraphUp,
  BsPeople,
  BsCheckCircle,
  BsXCircle,
} from "react-icons/bs";
import { useNavigate, useParams } from "react-router-dom";
import biometricDeviceService from "../../services/biometricDeviceService";
import { useDisclosure } from "@chakra-ui/react";
import DeviceDrawer from "../../components/biometricDevices/DeviceDrawer";

const DeviceDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();

  const bgColor = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.700");

  // State
  const [device, setDevice] = useState(null);
  const [health, setHealth] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [healthLoading, setHealthLoading] = useState(false);
  const [statsLoading, setStatsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [syncing, setSyncing] = useState(false);

  // Fetch data
  useEffect(() => {
    if (id) {
      fetchAllData();
    }
  }, [id]);

  // Auto-refresh health every 30 seconds
  useEffect(() => {
    if (!id) return;

    const interval = setInterval(() => {
      fetchHealth();
    }, 30000);

    return () => clearInterval(interval);
  }, [id]);

  const fetchAllData = async () => {
    await Promise.all([fetchDevice(), fetchHealth(), fetchStats()]);
  };

  const fetchDevice = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await biometricDeviceService.getDeviceById(id);
      setDevice(response.data);
    } catch (err) {
      console.error("Error fetching device:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchHealth = async () => {
    try {
      setHealthLoading(true);
      const response = await biometricDeviceService.getDeviceHealth(id);
      setHealth(response.data);
    } catch (err) {
      console.error("Error fetching health:", err);
    } finally {
      setHealthLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      setStatsLoading(true);
      const response = await biometricDeviceService.getDeviceStats(id);
      setStats(response.data);
    } catch (err) {
      console.error("Error fetching stats:", err);
    } finally {
      setStatsLoading(false);
    }
  };

  const handleSync = async () => {
    try {
      setSyncing(true);
      const response = await biometricDeviceService.syncDevice(id);
      toast({
        title: "Sync initiated",
        description: response.message || "Device sync in progress",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
      fetchDevice();
      fetchStats();
    } catch (err) {
      toast({
        title: "Sync failed",
        description: err.response?.data?.message || err.message,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setSyncing(false);
    }
  };

  const handleDeviceSuccess = () => {
    fetchDevice();
    onClose();
  };

  const getStatusColor = (status) => {
    const colors = {
      ACTIVE: "green",
      INACTIVE: "gray",
      MAINTENANCE: "orange",
      DISABLED: "red",
    };
    return colors[status] || "gray";
  };

  const getHealthIndicator = () => {
    if (!health?.isOnline) {
      return { color: "red.500", label: "Offline", icon: BsXCircle };
    }
    if (health?.avgResponseTime > 5000) {
      return { color: "yellow.500", label: "Slow", icon: BsCheckCircle };
    }
    return { color: "green.500", label: "Online", icon: BsCheckCircle };
  };

  const formatDate = (date) => {
    if (!date) return "Never";
    return new Date(date).toLocaleString();
  };

  const hasWritePermission = () => {
    const company = JSON.parse(localStorage.getItem("company") || "{}");
    const role = company.role;
    return ["SuperAdmin", "Admin", "HRHead", "HRExecutive"].includes(role);
  };

  if (loading) {
    return (
      <Box p={6}>
        <VStack spacing={4} align="stretch">
          <Skeleton height="60px" />
          <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={4}>
            <Skeleton height="120px" />
            <Skeleton height="120px" />
            <Skeleton height="120px" />
            <Skeleton height="120px" />
          </SimpleGrid>
          <Skeleton height="200px" />
          <Skeleton height="200px" />
        </VStack>
      </Box>
    );
  }

  if (error || !device) {
    return (
      <Box p={6}>
        <Alert status="error">
          <AlertIcon />
          Device not found or error loading device details
        </Alert>
        <Button mt={4} onClick={() => navigate("/attendance/biometric")}>
          Back to Devices
        </Button>
      </Box>
    );
  }

  const healthIndicator = getHealthIndicator();

  return (
    <Box p={6}>
      {/* Header */}
      <Flex justify="space-between" align="center" mb={6}>
        <HStack spacing={3}>
          <Button
            variant="ghost"
            leftIcon={<BsArrowLeft />}
            onClick={() => navigate("/attendance/biometric")}
          >
            Back
          </Button>
          <Divider orientation="vertical" h="30px" />
          <HStack>
            <BsShieldCheck size={24} />
            <Heading size="lg">{device.deviceName}</Heading>
            <Badge colorScheme={getStatusColor(device.status)}>
              {device.status}
            </Badge>
          </HStack>
        </HStack>

        <HStack spacing={2}>
          {hasWritePermission() && (
            <>
              <Button
                leftIcon={<BsPencil />}
                variant="outline"
                onClick={onOpen}
              >
                Edit
              </Button>
              <Button
                leftIcon={<BsArrowRepeat />}
                colorScheme="blue"
                onClick={handleSync}
                isLoading={syncing}
              >
                Manual Sync
              </Button>
            </>
          )}
        </HStack>
      </Flex>

      {/* Stats Cards */}
      <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={4} mb={6}>
        {/* Health Status */}
        <Card bg={bgColor} borderColor={borderColor} borderWidth="1px">
          <CardBody>
            <Stat>
              <StatLabel>Health Status</StatLabel>
              <HStack mt={2} spacing={3}>
                <Icon
                  as={healthIndicator.icon}
                  boxSize={6}
                  color={healthIndicator.color}
                />
                <StatNumber fontSize="xl">{healthIndicator.label}</StatNumber>
              </HStack>
              <StatHelpText>
                {health?.lastHeartbeat
                  ? `Last: ${formatDate(health.lastHeartbeat)}`
                  : "No heartbeat data"}
              </StatHelpText>
            </Stat>
          </CardBody>
        </Card>

        {/* Response Time */}
        <Card bg={bgColor} borderColor={borderColor} borderWidth="1px">
          <CardBody>
            <Stat>
              <StatLabel>Avg Response Time</StatLabel>
              <StatNumber>
                {healthLoading ? (
                  <Skeleton height="30px" width="80px" />
                ) : (
                  `${health?.avgResponseTime || 0}ms`
                )}
              </StatNumber>
              <StatHelpText>
                {health?.avgResponseTime > 3000 ? "⚠️ Slow" : "✓ Good"}
              </StatHelpText>
            </Stat>
          </CardBody>
        </Card>

        {/* Total Logs */}
        <Card bg={bgColor} borderColor={borderColor} borderWidth="1px">
          <CardBody>
            <Stat>
              <StatLabel>Total Logs</StatLabel>
              <StatNumber>
                {statsLoading ? (
                  <Skeleton height="30px" width="80px" />
                ) : (
                  stats?.totalLogs || 0
                )}
              </StatNumber>
              <StatHelpText>All time</StatHelpText>
            </Stat>
          </CardBody>
        </Card>

        {/* Mapped Users */}
        <Card bg={bgColor} borderColor={borderColor} borderWidth="1px">
          <CardBody>
            <Stat>
              <StatLabel>Mapped Users</StatLabel>
              <StatNumber>
                {statsLoading ? (
                  <Skeleton height="30px" width="80px" />
                ) : (
                  stats?.mappedUsers || 0
                )}
              </StatNumber>
              <StatHelpText>Active mappings</StatHelpText>
            </Stat>
          </CardBody>
        </Card>
      </SimpleGrid>

      <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6}>
        {/* Device Information */}
        <Card bg={bgColor} borderColor={borderColor} borderWidth="1px">
          <CardHeader>
            <Heading size="md">Device Information</Heading>
          </CardHeader>
          <CardBody>
            <VStack align="stretch" spacing={3}>
              <Flex justify="space-between">
                <Text fontWeight="medium">Device Code:</Text>
                <Text>
                  <code>{device.deviceCode}</code>
                </Text>
              </Flex>
              <Divider />
              <Flex justify="space-between">
                <Text fontWeight="medium">Vendor:</Text>
                <Badge colorScheme="blue">{device.vendor}</Badge>
              </Flex>
              <Divider />
              <Flex justify="space-between">
                <Text fontWeight="medium">IP Address:</Text>
                <Text>{device.ipAddress}</Text>
              </Flex>
              <Divider />
              <Flex justify="space-between">
                <Text fontWeight="medium">Port:</Text>
                <Text>{device.port}</Text>
              </Flex>
              <Divider />
              <Flex justify="space-between">
                <Text fontWeight="medium">Serial Number:</Text>
                <Text>{device.serialNumber || "-"}</Text>
              </Flex>
              <Divider />
              <Flex justify="space-between">
                <Text fontWeight="medium">Location:</Text>
                <Text>{device.location || "-"}</Text>
              </Flex>
              <Divider />
              <Flex justify="space-between">
                <Text fontWeight="medium">Timezone:</Text>
                <Text>{device.timezone}</Text>
              </Flex>
            </VStack>
          </CardBody>
        </Card>

        {/* Sync Configuration */}
        <Card bg={bgColor} borderColor={borderColor} borderWidth="1px">
          <CardHeader>
            <Heading size="md">Sync Configuration</Heading>
          </CardHeader>
          <CardBody>
            <VStack align="stretch" spacing={3}>
              <Flex justify="space-between">
                <Text fontWeight="medium">Sync Mode:</Text>
                <Tag
                  colorScheme={device.syncMode === "PULL" ? "blue" : "purple"}
                >
                  {device.syncMode}
                </Tag>
              </Flex>
              <Divider />
              {device.syncMode === "PULL" && (
                <>
                  <Flex justify="space-between">
                    <Text fontWeight="medium">Sync Interval:</Text>
                    <Text>{device.syncInterval} minutes</Text>
                  </Flex>
                  <Divider />
                </>
              )}
              <Flex justify="space-between">
                <Text fontWeight="medium">Last Sync:</Text>
                <Text fontSize="sm">{formatDate(device.lastSyncAt)}</Text>
              </Flex>
              <Divider />
              <Flex justify="space-between">
                <Text fontWeight="medium">Last Sync Status:</Text>
                <Badge
                  colorScheme={
                    device.lastSyncStatus === "SUCCESS" ? "green" : "red"
                  }
                >
                  {device.lastSyncStatus || "N/A"}
                </Badge>
              </Flex>
              <Divider />
              <Flex justify="space-between">
                <Text fontWeight="medium">Created:</Text>
                <Text fontSize="sm">{formatDate(device.createdAt)}</Text>
              </Flex>
              <Divider />
              <Flex justify="space-between">
                <Text fontWeight="medium">Last Updated:</Text>
                <Text fontSize="sm">{formatDate(device.updatedAt)}</Text>
              </Flex>
            </VStack>
          </CardBody>
        </Card>
      </SimpleGrid>

      {/* Sync History Section (Future Enhancement) */}
      <Card bg={bgColor} borderColor={borderColor} borderWidth="1px" mt={6}>
        <CardHeader>
          <HStack justify="space-between">
            <Heading size="md">Recent Sync Activity</Heading>
            <Button
              size="sm"
              variant="ghost"
              onClick={fetchHealth}
              isLoading={healthLoading}
            >
              Refresh
            </Button>
          </HStack>
        </CardHeader>
        <CardBody>
          <Text color="gray.500" textAlign="center" py={4}>
            Sync history will be displayed here
          </Text>
        </CardBody>
      </Card>

      {/* Device Drawer */}
      <DeviceDrawer
        isOpen={isOpen}
        onClose={onClose}
        device={device}
        onSuccess={handleDeviceSuccess}
      />
    </Box>
  );
};

export default DeviceDetails;
